import React, { useState, useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import Checkbox from 'components/Checkbox';
import { Checkmark, Search } from 'react-ionicons';

export interface IFilterDropdownOption {
  label: string;
  onClick?: () => void;
  value: string;
}

interface FilterDropdownProps {
  label: string | React.ReactNode;
  options: IFilterDropdownOption[];
  className?: string;
  menuPosition?: 'bottom-right' | 'top-right';
  type?: 'multi' | 'single';
  onSelect?: (value: IFilterDropdownOption) => void;
  selected: IFilterDropdownOption['value'][];
  showSelectAll?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = observer(
  ({
    label,
    options,
    className,
    menuPosition = 'top-right',
    type = 'single',
    selected,
    onSelect,
    showSelectAll = false,
  }) => {
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const allOptionValues = useMemo(
      () => options.map((opt) => opt.value),
      [options],
    );

    const isAllSelected = selected.length === allOptionValues.length;

    const handleOptionClick = (option: IFilterDropdownOption) => {
      if (option.value === '__select_all__') {
        if (isAllSelected) {
          allOptionValues.forEach((value) => {
            if (selected.includes(value)) {
              const optionObj = options.find((opt) => opt.value === value);
              if (optionObj) onSelect?.(optionObj);
            }
          });
        } else {
          allOptionValues.forEach((value) => {
            if (!selected.includes(value)) {
              const optionObj = options.find((opt) => opt.value === value);
              if (optionObj) onSelect?.(optionObj);
            }
          });
        }
      } else {
        onSelect?.(option);
        if (typeof option.onClick === 'function') option.onClick();
        if (type === 'single') closeDropdown();
      }
    };

    const closeDropdown = () => setIsOpen(false);
    const toggleDropdown = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsOpen((prev) => !prev);
    };

    useEffect(() => {
      setFilteredOptions(options);
    }, [options]);

    useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        )
          closeDropdown();
      };

      window.addEventListener('click', handleOutsideClick);
      return () => {
        window.removeEventListener('click', handleOutsideClick);
      };
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value.toLowerCase();
      setFilteredOptions(
        options.filter((opt) => opt.label.toLowerCase().includes(searchTerm)),
      );
    };

    const finalOptions =
      type === 'multi' && showSelectAll
        ? [{ label: 'Select all', value: '__select_all__' }, ...filteredOptions]
        : filteredOptions;

    return (
      <div className={clsx('relative flex', className)} ref={dropdownRef}>
        <button
          onClick={toggleDropdown}
          className={`border-0 ${
            isOpen ? 'button-background-primary-default' : 'border-transparent'
          } font-body cursor-pointer ${
            isOpen
              ? 'text-button-background-primary-default'
              : 'text-content-base-default'
          } bg-background-medium rounded font-bold hover:text-button-background-primary-default ${
            isOpen ? 'bg-dark' : 'background-base-surface-2'
          }`}
        >
          <div className="flex w-full items-center justify-center gap-2">
            {typeof label === 'string'
              ? label
              : React.cloneElement(label as React.ReactElement, {
                  isSelected: isOpen,
                })}
          </div>
        </button>

        {isOpen && (
          <div
            className={clsx(
              `bg-opacity-85 absolute left-full z-[1000] ml-2 flex flex-col items-start justify-center gap-3 rounded bg-navigation-surface-primary p-3 shadow-elevation3 backdrop-blur-lg`,
              {
                'top-0': menuPosition === 'top-right',
                'bottom-0': menuPosition === 'bottom-right',
              },
            )}
          >
            <p className="text-sm font-bold text-content-base-default">
              Filters
            </p>
            <div className="flex w-full items-center justify-center gap-1 border-y border-stroke-base-subdued py-2">
              <span className="text-content-base-subdued">
                <Search color="currentColor" width="16px" height="16px" />
              </span>
              <input
                ref={inputRef}
                onChange={handleSearchChange}
                placeholder="Search"
                className="bg-transparent text-sm placeholder:text-input-content-unfilled hover:text-input-content-hover focus:text-input-content-focus focus:outline-none"
              />
            </div>
            <div className="w-full space-y-1">
              {finalOptions.map((option, optionIndex) => (
                <div
                  key={optionIndex}
                  onClick={() => handleOptionClick(option)}
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-2 self-stretch rounded p-2 text-sm font-semibold text-content-base-default transition-all duration-300 ease-out hover:bg-navigation-surface-primary-hover',
                    { 'justify-between': type === 'single' },
                  )}
                  title={option.label}
                >
                  {type === 'multi' && (
                    <Checkbox
                      checked={
                        option.value === '__select_all__'
                          ? isAllSelected
                          : selected.includes(option.value)
                      }
                    />
                  )}
                  <p className="inline-flex">
                    <span className="truncate">{option.label}</span>
                  </p>
                  {type === 'single' && selected.includes(option.value) ? (
                    <span className="text-input-stroke-selected">
                      <Checkmark color="currentColor" />
                    </span>
                  ) : null}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="w-full py-2 text-center text-content-base-subdued">
                  No result
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default FilterDropdown;
